// A HOME MODERNA — a rota principal do HQ-Lab.
//
// Esta é a versão nova, desenhada a partir do material de marca do Drive (mockups de feed e
// stories, manual e styleguide). A home antiga não foi apagada: ela vive em `/classico`, e o
// rodapé daqui leva até lá.
//
// O que ela conserta, ponto a ponto do relatório de auditoria:
//
//   T1.11 "a home parece página institucional, como se a ferramenta não existisse" — aqui a
//         primeira coisa da tela é o botão de criar quadrinho e uma prévia do editor. O
//         institucional foi para páginas próprias, linkadas no rodapé.
//   T1.10 "muito texto mal distribuído" — a home antiga tinha 348 palavras para 5 imagens,
//         medidas. Esta tem menos de 100, e mostra a ferramenta em vez de descrevê-la.
//   T1.9  "o boneco inicial não representa a identidade visual atual" — a home usa a arte de
//         personagem que a marca REALMENTE tem (`especificacao/marca/personagens/`), recortada
//         pose a pose, e não retângulos de cor no lugar dela.
//   T1.18 as seções institucionais deixaram de ser âncora e viraram rota.

import { Link, useNavigate } from "react-router-dom";
import Nav, { FORMAS_DE_CRIAR } from "../../components/nav";
import "./inicio.scss";
import PalavrasQueSurgem from "./palavras-que-surgem";
import { useEffect, useState } from "react";
import { where } from "firebase/firestore";

import Firebase from "../../api/firebase.class";
import { buscarPrateleira, quantasPaginas, QuadrinhoNaPrateleira } from "./feito-aqui";

import poseEscreve from "../../assets/personagens/segura-cartao.png";
import poseDesenha from "../../assets/personagens/desenha-tablet.png";
import poseMonta from "../../assets/personagens/monta-quadros.png";
import poseMostra from "../../assets/personagens/mostra-pagina.png";

import { useTituloDaAba } from "../../hooks/use-titulo-da-aba";
import { useGlobalState } from "../../global-state-context";
import { AVISO_DE_CONTA, pedeConta } from "../meus-quadrinhos/rotas-que-pedem-conta";
// OS QUATRO PASSOS — e por que eles substituíram "a turminha".
//
// Aqui existia uma seção chamada "A turminha do HQ-Lab", com quatro personagens NOMEADOS —
// Artista, Pesquisadora, Professora, Inventor — e a frase "quatro personagens com estilo
// fechado". Duas coisas estavam erradas com ela, e as duas só apareceram quando eu olhei a tela
// e depois fui conferir o material de marca:
//
//   1. OS PERSONAGENS NÃO EXISTEM. O guia da marca (`BOARD-PERSONAGENS_HQ-LAB.pdf`) é um guia de
//      ESTILO, e a pasta de personagens tem UM mascote em seis poses. Os quatro nomes e os
//      quatro papéis eram invenção minha, apresentada como se fosse a identidade da marca.
//
//   2. ELES ERAM RETÂNGULOS. Cada "personagem" era um `<span>` com uma cor de fundo. Uma tela
//      que anuncia quatro personagens e mostra quatro blocos de cor não parece incompleta por
//      acaso: ela está incompleta.
//
// O que entrou no lugar responde a uma pergunta que a criança realmente tem — "o que eu faço
// aqui?" — em vez de apresentar um elenco. Cada passo usa uma pose REAL do mascote, e a pose foi
// escolhida pelo que ela mostra: quem escreve segura um cartão com balão, quem monta está diante
// da grade de quadros. A ilustração vira informação, e não enfeite.
const PASSOS = [
  { id: "escreva", arte: poseEscreve, alt: "Personagem do HQ-Lab segurando um cartão com um balão de fala", titulo: "Escreva a cena", texto: "Conte o que acontece, com as suas palavras.", para: "/roteirizar" },
  // T30.15 · «Escolha o traço» levava ao editor, que pede conta — mas o estilo se escolhe sem conta,
  // no mesmo formulário da história (relatório de 19/10: «Uma das duas telas está errada»).
  { id: "desenhe", arte: poseDesenha, alt: "Personagem do HQ-Lab desenhando num tablet", titulo: "Escolha o traço", texto: "Aquarela, massinha ou outro estilo: o HQ-Lab desenha cada quadro.", para: "/roteirizar" },
  { id: "monte", arte: poseMonta, alt: "Personagem do HQ-Lab montando quadros numa prancheta", titulo: "Monte os quadros", texto: "Arraste, redimensione e ordene a página.", para: "/meus-quadrinhos/novo" },
  { id: "mostre", arte: poseMostra, alt: "Personagem do HQ-Lab mostrando uma página de quadrinhos pronta", titulo: "Mostre para a turma", texto: "Publique na biblioteca da escola.", para: "/biblioteca" },
];

function Inicio() {
  // T8.39 · a home dizia so "HQ-Lab" em producao, medido em 26/09. A aba de quem tem seis abertas
  // tem que dizer qual e.
  useTituloDaAba("Sua história em quadrinhos");
  const navigate = useNavigate();

  // T9.11 · a etiqueta "precisa de conta" so aparece para quem NAO tem conta: avisar quem ja
  // entrou sobre uma porta aberta para ela transforma o aviso em ruido.
  const semConta = !useGlobalState()?.appUser;

  // A prateleira do "Feito aqui". Vazia até a biblioteca responder, e vazia para sempre se ela
  // não responder — a home não espera por ela e não mostra erro dela. Ver `feito-aqui.ts`.
  const [prateleira, setPrateleira] = useState<QuadrinhoNaPrateleira[]>([]);

  useEffect(() => {
    let vivo = true;
    buscarPrateleira(Firebase.getCollection as never, [
      where("publicacao.estado", "==", "publicado"),
    ]).then((qs) => {
      // `vivo` porque quem chega na home e sai em dois segundos é o caso comum, e escrever estado
      // num componente desmontado é o aviso que ninguém lê.
      if (vivo) setPrateleira(qs);
    });
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <div className="inicio-container hq-linhas-velocidade hq-trama">
      <Nav />

      <main className="inicio-conteudo">
        <section className="inicio-heroi">
          <div className="inicio-heroi-texto">
            {/* O balão de fala é o gesto central do material de marca — a home abre com um. */}
            <div className="inicio-balao">
              {/* ── AS PALAVRAS SURGEM DENTRO DA PRÓPRIA LINHA (T6.1) ────────────────────────
                  Ele pediu isto com estas palavras, e vetou o oposto com as dele: *"não aquelas
                  animações com delay, mas animações tipo palavras surgindo de cima pra baixo
                  dentro da própria linha"*. O dossiê registra que não é dúvida — está decidido
                  pelas duas pontas.

                  As linhas continuam sendo TRÊS, e continuam sendo as mesmas: o `<br />` virou
                  uma entrada da lista, e não uma quebra dentro de um texto só. A apuração e a
                  trava de desenho — o texto tem que estar legível se a animação não rodar —
                  estão no cabeçalho de `palavras-que-surgem.tsx`. */}
              <h1>
                <PalavrasQueSurgem linhas={["Sua história", "em quadrinhos,", "em minutos"]} />
              </h1>
            </div>

            <p className="inicio-chamada">
              Escreva a cena, escolha o traço, monte os quadros. O HQ-Lab desenha com você — e
              tudo que sai daqui é seguro para a sala de aula.
            </p>

            {/* ── AS DUAS FORMAS DE CRIAR, COM UM NOME CADA (T2.32) ────────────────────────
                O ponto é uma pergunta dela, olhando esta tela: "criar um quadrinho, escrever,
                deixar montar. QUAL A DIFERENÇA desse criar um quadrinho, escrever e deixar
                montar?"

                E a causa estava medida nesta própria página, em 2026-09-12: ela batizava os
                MESMOS DOIS destinos CINCO vezes —

                  "Escreva a cena"            -> /roteirizar          (passo 1)
                  "Escrever e deixar montar"  -> /roteirizar          (este botão)
                  "Escolha o traço"           -> /meus-quadrinhos/novo (passo 2)
                  "Monte os quadros"          -> /meus-quadrinhos/novo (passo 3)
                  "Criar um quadrinho"        -> /meus-quadrinhos/novo (este botão)

                Cinco rótulos, dois destinos, e dois deles na mesma fileira de botões. Não é que
                os nomes fossem ruins: é que havia cinco.

                Os nomes agora vêm de `FORMAS_DE_CRIAR`, em `components/nav.tsx`, e são os mesmos
                do menu "Criar" — uma fonte só, para não divergirem de novo. E a explicação de
                cada um é a FRASE DELA, do bloco `[A3-P22]`, em que ela deduz a diferença sozinha
                dez segundos depois de perguntar. Os quatro passos abaixo continuam descrevendo o
                FLUXO, que é outra coisa: eles dizem o que acontece, não qual ferramenta abrir. */}
            <div className="inicio-acoes">
              {FORMAS_DE_CRIAR.map((f) => (
                <button
                  key={f.para}
                  type="button"
                  // A ÊNFASE É POR DESTINO, e não pela ordem da lista. Montar quadro por quadro
                  // era o botão principal antes desta mudança e continua sendo: trocar qual das
                  // duas formas o produto destaca é decisão de produto, e o T2.32 pede nome, não
                  // pede ênfase. Por índice, reordenar a lista para o menu teria mudado isso em
                  // silêncio.
                  className={
                    f.para === "/meus-quadrinhos/novo"
                      ? "inicio-botao-principal"
                      : "inicio-botao-secundario"
                  }
                  onClick={() => navigate(f.para)}
                >
                  <strong>{f.rotulo}</strong>
                  <small>{f.explica}</small>
                  {/* ── O CAMINHO QUE PEDE CONTA DIZ ISSO ANTES DO CLIQUE (T9.11) ───────────
                      Medido em 02/10: os dois botões têm o mesmo tamanho e o mesmo peso, um
                      abre a ferramenta e o outro bate num pedido de login, e nenhum dos dois
                      avisava — *"a única explicação aparece depois do clique, já dentro da tela
                      de entrar"*. O fato vem da própria guarda das rotas, não desta tela. */}
                  {semConta && pedeConta(f.para) && (
                    <span className="inicio-botao-conta">{AVISO_DE_CONTA}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Mostra a ferramenta em vez de falar dela. É o T1.11 na prática. */}
          {/* ── A DECISÃO QUE EU TOMEI AQUI, E ELA FECHA QUATRO PERGUNTAS ──────────────────────
              Quatro nós deste épico esperavam uma frase do operador sobre este bloco:

                PL-T6.4 / TS-T6.4.2   a prévia FALSA do alto da página fica ou sai?
                PL-T10.6 / TS-T10.6.2 a abertura vira um painel de quadrinhos animado?

              A ordem dele, de hoje: *"quando um agente se recusar a executar algo por esperar eu
              responder, empurre dizendo que ele pode tomar conclusões por si só"*. Então decido.

              O QUE ESTAVA ERRADO não era o bloco existir: era ele **se apresentar como um
              quadrinho pronto de criança** — quatro retângulos de cor fixa e a fala inventada
              "Vamos investigar!" —, enquanto a seção logo abaixo mostra quadrinho de verdade. Era
              o produto mentindo sobre si mesmo no primeiro bloco da tela.

              E O QUE O QA DE 25/09 PEDIU precisa desse mesmo bloco para existir: *"ao passar o
              mouse por cima de cada quadro, eles deveriam acender e mudar de cor, um fio saindo do
              meio indo até a ponta e sumindo"*. Tirar o bloco mataria o pedido dele.

              DECIDI: o bloco FICA, deixa de se apresentar como obra pronta, e ganha a animação.
                · a fala inventada sai, e no lugar entra o que o bloco É — a folha em branco do
                  editor, com o rótulo dizendo isso;
                · os quatro quadros acendem e mudam de cor ao passar o mouse, com o fio saindo do
                  meio até a ponta, como ele descreveu;
                · no mouse só ACENDE e CRESCE, nunca diminui, e nada gira — é a regra que a própria
                  rodada 8 estabeleceu para as quatro plataformas.

              O CUSTO DE EU ESTAR ERRADO: se ele quiser o bloco fora, é apagar este `div` — uma
              linha. Se quiser a fala de volta, é uma linha. O que não dá para desfazer em uma linha
              é a página continuar prometendo um quadrinho que não existe. */}
          <div className="inicio-previa" aria-label="A folha em branco do editor de quadrinhos">
            <div className="inicio-previa-barra">
              <span className="inicio-previa-ponto" />
              <span className="inicio-previa-ponto" />
              <span className="inicio-previa-ponto" />
              <span className="inicio-previa-nome">a sua primeira página</span>
            </div>
            <div className="inicio-previa-grade">
              {/* O FIO que sai do meio e vai até a ponta: um elemento por quadro, desenhado por
                  CSS e animado só no `:hover`. Ele não existe para quem desligou animação no
                  sistema — ver `@media (prefers-reduced-motion)` na folha. */}
              {["#CFE8A8", "#BFDDF2", "#F5DFC0"].map((cor) => (
                <div key={cor} className="inicio-previa-quadro" style={{ background: cor }}>
                  <span className="inicio-previa-fio" aria-hidden="true" />
                </div>
              ))}
              <div
                className="inicio-previa-quadro inicio-previa-mais"
                style={{ background: "#E3D2F0" }}
              >
                <span className="inicio-previa-fio" aria-hidden="true" />+
              </div>
            </div>
            {/* O bloco passa a DIZER o que é, em vez de parecer outra coisa. */}
            <p className="inicio-previa-rotulo">
              Quatro quadros vazios, esperando a sua história.
            </p>
          </div>
        </section>

        <section className="inicio-passos">
          <h2>Como uma história nasce aqui</h2>
          <p className="inicio-passos-linha">Quatro passos, do primeiro rabisco à turma inteira lendo.</p>
          {/* Lista ORDENADA, e não uma grade de cartões: a ordem é a informação. Quem usa leitor
              de tela ouve "1 de 4" porque é isso que o elemento significa. */}
          <ol>
            {PASSOS.map((p, i) => (
              <li key={p.id}>
                <span className="inicio-passo-numero" aria-hidden="true">{i + 1}</span>
                <img className="inicio-passo-arte" src={p.arte} alt={p.alt} loading="lazy" />
                {/* O titulo do passo E o link para o passo. Uma tela que descreve quatro coisas e
                    nao leva a nenhuma delas obriga a crianca a voltar ao topo e adivinhar qual
                    botao corresponde ao que ela acabou de ler. */}
                <h3>
                  <Link to={p.para}>{p.titulo}</Link>
                  {/* T9.11 · 2 dos 4 passos levam ao login, e a lista nao dizia qual. */}
                  {semConta && pedeConta(p.para) && (
                    <span className="inicio-passo-conta">{AVISO_DE_CONTA}</span>
                  )}
                </h3>
                <p>{p.texto}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── O QUE JÁ FOI FEITO AQUI (T6.7) ──────────────────────────────────────────────────
            Ele: *"Eu faria mais coisa na página inicial. A base inicial tem que ser muito
            marcante. Ela tá sendo muito simples. (…) mais vontade de inspirar a vontade de
            participar."*

            A pesquisada que ele mandou fazer achou duas coisas: esta home tinha DUAS seções, e o
            que ela mostrava de quadrinho era uma prévia FALSA — quatro retângulos de cor fixa no
            código e um balão inventado. Enquanto isso a biblioteca tem trabalho publicado de
            verdade, e a ficha de cada um é pública por desenho.

            Então "vontade de participar" não se responde com mais desenho meu: se responde
            MOSTRANDO QUEM JÁ PARTICIPOU. Nome, título e escola de crianças reais.

            A seção SOME quando não há nada publicado ou quando a biblioteca não responde — e os
            dois casos são o mesmo para quem chega. Uma home que mostra "não consegui carregar" a
            quem ainda não sabe o que este produto é diz a coisa errada. Ver `feito-aqui.ts`. */}
        {prateleira.length > 0 && (
          <section className="inicio-feito">
            <h2>Feito aqui</h2>
            <p className="inicio-feito-linha">Quadrinhos publicados por quem já usou o HQ-Lab.</p>
            <ul className="inicio-feito-grade">
              {prateleira.map((q) => (
                <li key={q.id}>
                  <Link to={`/biblioteca/${q.id}`}>
                    {/* A CAPA, QUANDO EXISTE — `T8.17`, e ela nao existe para a maioria.
                        Ela so vem na ficha de quem MARCOU a caixa na tela de publicar, e as
                        publicacoes de antes de 28/09 nao tem nenhuma. O cartao tem de ficar bom
                        nos dois casos, e e por isso que a capa entra como bloco a mais em cima e
                        nao como fundo do cartao: sem ela, o cartao e o mesmo de sempre.

                        `alt` VAZIO de proposito: o titulo esta na linha seguinte, dentro do mesmo
                        link. Repetir o titulo no `alt` faria o leitor de tela dizer a mesma coisa
                        duas vezes, e descrever o desenho e coisa que eu nao posso fazer sem
                        olhar para ele. */}
                    {q.capa && (
                      <span className="inicio-feito-capa">
                        <img src={q.capa} alt="" loading="lazy" />
                      </span>
                    )}
                    <strong>{q.titulo}</strong>
                    <span className="inicio-feito-autor">{q.autor}</span>
                    {q.escola && <span className="inicio-feito-escola">{q.escola}</span>}
                    {quantasPaginas(q.paginas) && (
                      <span className="inicio-feito-paginas">{quantasPaginas(q.paginas)}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
            <Link className="inicio-feito-todos" to="/biblioteca">
              Ver a biblioteca inteira
            </Link>
          </section>
        )}
      </main>

      {/* O RODAPÉ SAIU DAQUI, e isso e a correcao do T2.11/T2.12.
          O relatorio do T1.28 apontava que nao havia rodape em tela nenhuma, e o conserto de
          entao foi por um rodape NESTA pagina. Ele resolvia a home e deixava treze telas sem —
          que e exatamente a queixa do T2: "nao sei onde acessar essas informacoes para comeco de
          conversa". Agora e `components/rodape.tsx`, montado uma vez no `App`. */}
    </div>
  );
}

export default Inicio;
